import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  CircularProgress,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { filamentService } from '../services/api';
import PageBackHeader from '../components/PageBackHeader';
import NomenclatureUsageDialog, { checkNomenclatureDelete } from '../components/NomenclatureUsageDialog';

function formatDrfErrors(data) {
  if (data == null) return null;
  if (typeof data === 'string') return data;
  if (data.detail != null) {
    return Array.isArray(data.detail) ? data.detail.join('\n') : String(data.detail);
  }
  const lines = [];
  Object.keys(data).forEach((key) => {
    const v = data[key];
    if (v == null) return;
    if (Array.isArray(v)) {
      const label = key === 'article' ? 'Артикул' : key === 'non_field_errors' ? '' : key;
      lines.push(label ? `${label}: ${v.join(' ')}` : v.join(' '));
    } else if (typeof v === 'object') {
      lines.push(`${key}: ${JSON.stringify(v)}`);
    } else {
      lines.push(`${key}: ${v}`);
    }
  });
  return lines.length ? lines.join('\n') : JSON.stringify(data);
}

function FilamentPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [mergeCandidate, setMergeCandidate] = useState(null);
  const [pendingPayload, setPendingPayload] = useState(null);
  const [usageDialog, setUsageDialog] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    supplier_name: '',
    article: '',
    supplier_article: '',
    description: '',
    minimum_stock: '',
    unit_price: '',
  });

  useEffect(() => { loadData(); }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const response = await filamentService.getAll();
      setItems(response.data.results || response.data);
    } catch (error) {
      console.error('Помилка завантаження:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleOpen = (item = null) => {
    if (item) {
      setEditing(item.id);
      setFormData({
        name: item.name,
        supplier_name: item.supplier_name || '',
        article: item.article || '',
        supplier_article: item.supplier_article || '',
        description: item.description || '',
        minimum_stock: item.minimum_stock != null ? String(item.minimum_stock) : '',
        unit_price: item.unit_price != null ? String(item.unit_price) : '',
      });
    } else {
      setEditing(null);
      setFormData({ name: '', supplier_name: '', article: '', supplier_article: '', description: '', minimum_stock: '', unit_price: '' });
    }
    setOpen(true);
  };

  const handleClose = () => { setOpen(false); setEditing(null); };

  const handleSubmit = async () => {
    try {
      const upStr = String(formData.unit_price ?? '').trim().replace(',', '.');
      let unit_price = null;
      if (upStr !== '') {
        const n = parseFloat(upStr);
        if (!Number.isFinite(n) || n < 0) {
          alert('Вкажіть коректну базову ціну (невід’ємне число) або залиште поле порожнім');
          return;
        }
        unit_price = n;
      }
      const articleTrimmed = String(formData.article ?? '').trim();
      const articleNorm = articleTrimmed === '' ? null : articleTrimmed;

      if (articleNorm != null) {
        const dup = items.find(
          (m) => String(m.article ?? '').trim() === articleNorm && (!editing || Number(m.id) !== Number(editing)),
        );
        if (dup) {
          alert(`Артикул уже використовується в іншій позиції («${dup.name}»). Вкажіть інший артикул або залиште поле порожнім.`);
          return;
        }
      }

      const payload = {
        name: formData.name,
        supplier_name: formData.supplier_name || '',
        article: articleNorm,
        supplier_article: formData.supplier_article || '',
        description: formData.description || '',
        unit_price,
        minimum_stock: formData.minimum_stock === '' || formData.minimum_stock == null ? null : Number(formData.minimum_stock),
      };

      const nameTrim = (formData.name || '').trim().toLowerCase();
      const supNameTrim = (formData.supplier_name || '').trim().toLowerCase();
      const mergeMatch = items.find((m) => {
        if (editing && Number(m.id) === Number(editing)) return false;
        const mName = (m.name || '').trim().toLowerCase();
        const mSupName = (m.supplier_name || '').trim().toLowerCase();
        return (supNameTrim && mName && supNameTrim === mName) || (nameTrim && mSupName && nameTrim === mSupName);
      });
      if (mergeMatch) {
        setMergeCandidate(mergeMatch);
        setPendingPayload({ payload, editing });
        return;
      }

      if (editing) { await filamentService.update(editing, payload); }
      else { await filamentService.create(payload); }
      handleClose();
      loadData();
    } catch (error) {
      const msg = formatDrfErrors(error.response?.data) || error.response?.statusText || error.message || 'Помилка збереження даних';
      alert(msg);
    }
  };

  const handleSaveSeparate = async () => {
    if (!pendingPayload) return;
    const { payload, editing: ed } = pendingPayload;
    setMergeCandidate(null);
    setPendingPayload(null);
    try {
      if (ed) { await filamentService.update(ed, payload); }
      else { await filamentService.create(payload); }
      handleClose();
      loadData();
    } catch (error) {
      const msg = formatDrfErrors(error.response?.data) || error.message || 'Помилка збереження';
      alert(msg);
    }
  };

  const handleMerge = async () => {
    if (!mergeCandidate || !pendingPayload) return;
    const { payload, editing: ed } = pendingPayload;
    setMergeCandidate(null);
    setPendingPayload(null);
    try {
      const merged = {
        ...payload,
        name: payload.name || mergeCandidate.name || '',
        supplier_name: payload.supplier_name || mergeCandidate.supplier_name || '',
        article: payload.article || mergeCandidate.article || null,
        supplier_article: payload.supplier_article || mergeCandidate.supplier_article || '',
        description: payload.description || mergeCandidate.description || '',
        unit_price: payload.unit_price ?? mergeCandidate.unit_price ?? null,
        minimum_stock: payload.minimum_stock ?? mergeCandidate.minimum_stock ?? null,
      };
      if (ed) {
        await filamentService.update(ed, merged);
        await filamentService.delete(mergeCandidate.id);
      } else {
        await filamentService.update(mergeCandidate.id, merged);
      }
      handleClose();
      loadData();
    } catch (error) {
      const msg = formatDrfErrors(error.response?.data) || error.message || 'Помилка злиття';
      alert(msg);
    }
  };

  const handleDelete = async (id) => {
    const item = items.find((x) => x.id === id);
    const itemName = item?.name || '';
    try {
      const check = await checkNomenclatureDelete(filamentService, id, {
        itemName,
        confirmMessage: 'Ви впевнені, що хочете видалити цей запис?',
      });
      if (check.blocked) {
        setUsageDialog({ usage: check.usage, itemName: check.itemName || itemName });
        return;
      }
      if (!check.confirmed) return;
      await filamentService.delete(id);
      loadData();
    } catch (error) {
      const data = error.response?.data;
      if (error.response?.status === 409 && data?.in_use) {
        setUsageDialog({ usage: data, itemName: data.name || itemName });
        return;
      }
      alert(formatDrfErrors(data) || 'Помилка видалення');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <PageBackHeader />
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Філамент</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
          Додати філамент
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Назва</TableCell>
              <TableCell>Назва постачальника</TableCell>
              <TableCell>Артикул</TableCell>
              <TableCell>Артикул постачальника</TableCell>
              <TableCell>Базова ціна (грн)</TableCell>
              <TableCell>Мін. залишок</TableCell>
              <TableCell>Опис</TableCell>
              <TableCell>Дії</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item) => (
              <TableRow key={item.id}>
                <TableCell>{item.name || '—'}</TableCell>
                <TableCell>{item.supplier_name || '—'}</TableCell>
                <TableCell>{item.article || '—'}</TableCell>
                <TableCell>{item.supplier_article || '—'}</TableCell>
                <TableCell>{item.unit_price != null ? item.unit_price : '—'}</TableCell>
                <TableCell>{item.minimum_stock != null ? item.minimum_stock : '—'}</TableCell>
                <TableCell>{item.description || '—'}</TableCell>
                <TableCell>
                  <IconButton onClick={() => handleOpen(item)}><EditIcon /></IconButton>
                  <IconButton onClick={() => handleDelete(item.id)}><DeleteIcon /></IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Редагувати філамент' : 'Додати філамент'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField label="Назва" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} fullWidth />
            <TextField label="Назва постачальника" value={formData.supplier_name} onChange={(e) => setFormData({ ...formData, supplier_name: e.target.value })} fullWidth />
            <TextField label="Артикул" value={formData.article} onChange={(e) => setFormData({ ...formData, article: e.target.value })} fullWidth helperText="Унікальний; порожньо — без артикула" />
            <TextField label="Артикул постачальника" value={formData.supplier_article} onChange={(e) => setFormData({ ...formData, supplier_article: e.target.value })} fullWidth />
            <TextField label="Базова ціна (грн)" type="number" inputProps={{ min: 0, step: 'any' }} value={formData.unit_price} onChange={(e) => setFormData({ ...formData, unit_price: e.target.value })} fullWidth />
            <TextField label="Мінімальний залишок" type="number" inputProps={{ min: 0, step: 'any' }} value={formData.minimum_stock} onChange={(e) => setFormData({ ...formData, minimum_stock: e.target.value })} fullWidth />
            <TextField label="Опис" multiline rows={3} value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} fullWidth />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Скасувати</Button>
          <Button onClick={handleSubmit} variant="contained">Зберегти</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(mergeCandidate)} onClose={() => { setMergeCandidate(null); setPendingPayload(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>Знайдено схожу позицію</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>Назва або назва постачальника збігається з існуючою позицією:</Typography>
          <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1, my: 1 }}>
            <Typography><b>Назва:</b> {mergeCandidate?.name || '—'}</Typography>
            <Typography><b>Назва постачальника:</b> {mergeCandidate?.supplier_name || '—'}</Typography>
            <Typography><b>Артикул:</b> {mergeCandidate?.article || '—'}</Typography>
            <Typography><b>Артикул постачальника:</b> {mergeCandidate?.supplier_article || '—'}</Typography>
          </Box>
          <Typography variant="body2" color="text.secondary">
            Об'єднати в одну картку (пусті поля заповняться з іншої позиції) або зберегти як окремі?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setMergeCandidate(null); setPendingPayload(null); }}>Скасувати</Button>
          <Button onClick={handleSaveSeparate} variant="outlined">Зберегти окремо</Button>
          <Button onClick={handleMerge} variant="contained" color="primary">Об'єднати</Button>
        </DialogActions>
      </Dialog>

      <NomenclatureUsageDialog
        open={!!usageDialog}
        onClose={() => setUsageDialog(null)}
        usage={usageDialog?.usage}
        itemName={usageDialog?.itemName}
      />
    </Box>
  );
}

export default FilamentPage;
